import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
    setMenuOpen(false);
  };

  const go = (path) => {
    setMenuOpen(false);
    navigate(path);
  };

  return (
    <motion.header
      data-testid="site-nav"
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled || menuOpen ? "bg-[#0A0A0A]/90 backdrop-blur-md border-b border-white/10" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <button
          data-testid="nav-logo"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="font-display font-bold uppercase tracking-tight text-lg md:text-xl"
        >
          JG<span className="text-[#2E6BE6]">/</span>COACHING
        </button>

        <nav className="hidden md:flex items-center gap-10 text-xs uppercase tracking-[0.2em] text-white/70">
          <button data-testid="nav-home" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className="link-underline hover:text-white">Home</button>
          <button data-testid="nav-about" onClick={() => scrollTo("about-me")} className="link-underline hover:text-white">About</button>
          <button data-testid="nav-gallery" onClick={() => scrollTo("gallery")} className="link-underline hover:text-white">Gallery</button>
          <button data-testid="nav-book" onClick={() => scrollTo("book")} className="link-underline hover:text-white">Book</button>
          <Link data-testid="nav-members" to="/dashboard" className="link-underline hover:text-white">Members</Link>
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="nav-cta"
            onClick={() => scrollTo("book")}
            className="group hidden sm:flex items-center gap-2 bg-[#D4AF37] text-black px-4 md:px-5 py-2 md:py-2.5 text-xs uppercase tracking-[0.15em] font-semibold hover:bg-[#E3C264] transition-colors"
          >
            <span className="italic">Reserve your slot</span>
            <span className="transition-transform group-hover:translate-x-1">→</span>
          </button>
          <button
            data-testid="nav-menu-toggle"
            onClick={() => setMenuOpen((o) => !o)}
            aria-label="Menu"
            className="md:hidden text-white p-2 -mr-2"
          >
            {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {menuOpen && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="md:hidden overflow-hidden bg-[#0A0A0A]/95 backdrop-blur-md border-t border-white/10"
          >
            <div className="px-6 py-6 flex flex-col gap-1 text-sm uppercase tracking-[0.2em]">
              <button data-testid="m-nav-home" onClick={() => { setMenuOpen(false); window.scrollTo({ top: 0, behavior: "smooth" }); }} className="text-left py-3 border-b border-white/10 text-white/80">Home</button>
              <button data-testid="m-nav-about" onClick={() => scrollTo("about-me")} className="text-left py-3 border-b border-white/10 text-white/80">About</button>
              <button data-testid="m-nav-gallery" onClick={() => scrollTo("gallery")} className="text-left py-3 border-b border-white/10 text-white/80">Gallery</button>
              <button data-testid="m-nav-book" onClick={() => scrollTo("book")} className="text-left py-3 border-b border-white/10 text-white/80">Book</button>
              <button data-testid="m-nav-members" onClick={() => go("/dashboard")} className="text-left py-3 border-b border-white/10 text-white/80">Members login</button>
              <button data-testid="m-nav-signup" onClick={() => go("/signup")} className="text-left py-3 text-[#D4AF37] font-semibold">Sign up</button>
              <button
                data-testid="m-nav-cta"
                onClick={() => scrollTo("book")}
                className="mt-4 bg-[#D4AF37] text-black py-3.5 text-xs tracking-[0.15em] font-semibold italic"
              >
                Reserve your slot →
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
